/**
 * PdappIconCard — "choose a PDApp icon", IN the chat (#788, Chu's reviews).
 *
 * ── The shape, and why it changed twice ──────────────────────────────────────
 *
 * It began as a DIALOG opened over whichever editor the browser judged to be in
 * front. Deciding which editor that was is a question with a wrong answer, and
 * the wrong answer wrote an icon into a document nobody was looking at — so the
 * asking moved into the chat, where it needs no such decision.
 *
 * The second cut then built BOTH halves inline, in the column's width. Chu's
 * third review pulled the making back out: designing an icon is a rich act with
 * image sources, an emoji picker, a glyph search and per-stop colour controls,
 * and a 300px column is the wrong room for it. The fourth pulled the CHOICE out
 * too: **Customize opens the same "Add a PDApp icon" dialog as the editor's
 * toolbar** — Design one or Copy from a PDApp — so the card keeps only what a
 * chat column is good at: a PREVIEW of what you are about to get, and the
 * decision.
 *
 * The dialog is `React.lazy`-loaded through a deep subpath. It is heavy
 * (react-colorful, the emoji dataset, the drive picker, the app list) and
 * keeping it out of the chat's bundle is the whole point of that subpath —
 * importing eagerly to satisfy the rule and shipping the weight anyway would be
 * the rule observed and the reason missed.
 *
 * ── The options ──────────────────────────────────────────────────────────────
 *
 * Customize (under the preview's name) · Cancel · Confirm. **Confirm without
 * customizing is not a decline**: it proceeds with the DEFAULT icon, which is
 * what the preview has been showing all along. Cancel IS the decline — it
 * ANSWERS "Not now" (never a dismissal, which the tool would report as "closed
 * without an answer"), and the tool reports that no icon was chosen.
 *
 * It rides the same wire as the credential, A2UI and access cards: a pending
 * question the SPA recognises by its header (`parsePdappIconRequest`), answered
 * with `onReply(answers, result)` — the icon travelling back in the `_result`
 * side-channel. The agent then places the block; the card writes nothing and
 * touches no document.
 *
 * Theming: rendered inline in the message list (inside a ThemedArea), so
 * `themed-*` classes apply directly. The maker portals itself and themes itself.
 */
import { Suspense, lazy, useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Image as ImageIcon, Loader2, Pencil } from 'lucide-react'
import { designedIconMetadata, artDidNotTravel } from '@papan/collab/ui/pdapp-icon-metadata'
import type { ChosenIcon } from '@papan/collab/ui/pdapp-icon-dialog'
import { MiniappIcon, resolveMiniappIconGlyph } from '@papan/core-ui/ui'
import { defaultIconSpecFor, editableIconSpec, PDAPP_ICON_CHOOSE_ANSWER, PDAPP_ICON_DECLINE_ANSWER, type MiniappIconSpec, type Placement } from '@papan/types'
import { getActiveTenantId } from '@papan/api-client'
import type { PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'

/** The "Add a PDApp icon" dialog — the same one the editor's toolbar opens
 *  (Design one / Copy from a PDApp), so the two surfaces offer one experience.
 *  Lazy — see the module docblock: the chat pays for it only when opened. */
const AddPdappIconDialog = lazy(() =>
  import('@papan/collab/ui/pdapp-icon-dialog').then((m) => ({ default: m.AddPdappIconDialog })),
)

/** The name a designed icon travels under, and the one the default spec is
 *  derived from. Must match `designedIconMetadata`'s own constant, or the
 *  preview and the produced icon disagree about what they are drawing. */
const ICON_NAME = 'Icon'

/** Where the icon should land, when the workspace has an element selected.
 *  Rendered by the MODALS Customize opens (the maker's footer, the copy step's
 *  options row), never by the card — the card stays minimal. With nothing
 *  selected there is nothing to be relative to, and it goes at the end of the
 *  page. */
export interface PdappIconPlacement {
  label: string
  options: ReadonlyArray<{ value: Placement; label: string }>
  value: Placement
  onChange: (value: Placement) => void
}

interface Props {
  request: { id: string; note: string | null }
  /** Reply through the question pipe. `result` is the `_result` side-channel the
   *  worker reads the chosen icon from — the same mechanism the access card uses
   *  to report what the browser did. */
  onReply: (answers: string[][], result?: unknown) => Promise<void>
  /** `undefined` when nothing is selected — no control anywhere, because there
   *  is nothing to be relative to; the icon goes at the end of the page. */
  placement?: PdappIconPlacement
  /** The app being edited (the PDApp workspace's root), left out of the copy
   *  list — an app linking to itself is never what the author meant. */
  excludeFolderId?: string | null
}

export function PdappIconCard({ request, onReply, placement, excludeFolderId }: Props) {
  const { t } = useTranslation('agent')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Which answer is in flight. One at a time — both buttons are disabled while
   *  either is pending — and the spinner shows on the one that was pressed. */
  const [pending, setPending] = useState<'confirm' | 'cancel' | null>(null)
  const busy = pending !== null
  /** What the card is currently offering. `null` spec = the DEFAULT icon, so
   *  the preview is never empty and Confirm has something concrete to mean. */
  const [chosen, setChosen] = useState<{
    spec: MiniappIconSpec | null
    label: string
    /** Uploaded art, as `data:` URIs. Without threading these into the preview
     *  an image-backed icon draws as a bare tile — the confirmation would show a
     *  DIFFERENT icon from the one the model was handed. */
    assets: { bg?: string; fg?: string }
    /** A COPIED icon's portable metadata, kept whole. Confirming sends THIS,
     *  not a spec rebuilt from it — the ids and the link live here. */
    meta?: PortablePdappMetadata
    link?: boolean
    /** Made in the dialog's maker rather than copied — decides the wording. */
    designed?: boolean
  }>(() => ({ spec: null, label: '', assets: {} }))
  const defaultSpec = useMemo(() => defaultIconSpecFor(ICON_NAME), [])
  const currentLabel = chosen.label || t('pdappIcon.defaultLabel')
  /** Said BEFORE Confirm, while the author can still act on it: the export drops
   *  an uploaded picture over its ceiling and keeps the spec pointing at it, so
   *  the tile will draw without it. (After a reply the card is gone — the
   *  question leaves the pending list — so a warning there is never seen.) */
  const artWarning = !!chosen.meta && !chosen.link && artDidNotTravel(chosen.meta)

  /** One answer, whichever button: busy while it is in flight, the spinner on
   *  the button pressed, and a failure said in the card's own (translated) words
   *  rather than left looking like nothing happened. A reply that LANDS removes
   *  the question and with it this card; one that returns without landing leaves
   *  the card here, so the buttons are released either way. */
  const run = async (which: 'confirm' | 'cancel', fn: () => Promise<void>) => {
    setPending(which)
    setError(null)
    try {
      await fn()
    } catch (e) {
      console.warn('[pdapp-icon] reply failed', e)
      setError(t('pdappIcon.sendFailed'))
    } finally {
      setPending(null)
    }
  }

  /**
   * Confirm. Whatever the preview is showing is what leaves — a chosen icon goes
   * as the dialog's own metadata (ids and link intact); nothing chosen means the
   * DEFAULT, resolved the way every other producer resolves it, so a default made
   * here and one made from the editor are the same object.
   */
  const confirm = () => run('confirm', async () => {
    const meta = chosen.meta
      ?? designedIconMetadata(defaultSpec, {}, await resolveMiniappIconGlyph(defaultSpec, ICON_NAME))
    await onReply([[PDAPP_ICON_CHOOSE_ANSWER]], { meta, link: !!chosen.link, placement: placement?.value })
  })

  /**
   * DECLINE. It ANSWERS the question (`PDAPP_ICON_DECLINE_ANSWER`), which the tool
   * reads as "the user did not choose an icon" — never a dismissal: that is a
   * REJECT, which the tool can only report as "the prompt closed without an
   * answer" (the plan doc records exactly that bug for the old "Not now").
   */
  const cancel = () => run('cancel', async () => {
    await onReply([[PDAPP_ICON_DECLINE_ANSWER]])
  })

  /**
   * The dialog's answer — designed OR copied. Choosing does NOT answer the
   * question: it closes the dialog and updates the preview, and the card waits
   * on Confirm, which is the entire point of previewing before the assistant
   * acts on it. Sending here meant the turn moved on before anyone could look
   * at what they had made.
   */
  const choose = async (chosenIcon: ChosenIcon) => {
    const meta = chosenIcon.meta
    const spec = (meta.icon as MiniappIconSpec | undefined) ?? null
    // Wording it as a copy regardless reported `Copied "Icon"` for an icon the
    // author had just drawn.
    const designed = chosenIcon.info.kind === 'designed'
    const name = chosenIcon.info.name ?? meta.name
    setError(null)
    setChosen({
      spec,
      label: designed ? t('pdappIcon.customLabel') : name,
      assets: meta.icon?.assets ?? {},
      meta,
      link: !!chosenIcon.info.linked,
      designed,
    })
    setDialogOpen(false)
    return { ok: true }
  }

  return (
    <Shell>
      <>
          {/* The preview is the point of the card: it answers "what am I about
              to get" before anything is pressed — and Customize sits right
              under its name, because it changes THAT. */}
          {/* Sized as one unit: a 48px stack — the name pinned to its top edge,
              Customize (32px) to its bottom — with a slightly smaller tile
              centred on it (`items-center`), the same inset above and below. */}
          <div className="mt-3 flex items-center gap-3">
            <Preview spec={chosen.spec} assets={chosen.assets} size={42} />
            <div className="min-w-0 h-12 flex flex-col items-start justify-between">
              <p className="text-[10px] leading-3 font-medium themed-text truncate max-w-full" data-testid="pdapp-icon-current">{currentLabel}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDialogOpen(true)}
                data-testid="pdapp-icon-customize"
                className="h-8 px-3 text-xs font-medium rounded-md themed-btn-primary inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
              >
                <Pencil size={12} /> {t('pdappIcon.customize')}
              </button>
            </div>
          </div>

          {artWarning && (
            <p className="mt-2 text-[11px] text-amber-400" data-testid="pdapp-icon-warn">{t('pdappIcon.artMissing')}</p>
          )}

          {/* The decision, left-aligned with the card's content (the row is
              pulled out by Cancel's padding so its text lines up): Cancel
              declines by answering 'Not now', Confirm sends what the preview shows. */}
          <div className="mt-5 -ml-3 flex items-baseline justify-start gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => void cancel()}
              data-testid="pdapp-icon-cancel"
              className="px-3 py-1.5 text-xs rounded-lg themed-text-secondary hover:themed-text transition-colors inline-flex items-baseline disabled:opacity-40"
            >
              {pending === 'cancel' && <Loader2 size={12} className="animate-spin mr-1.5 self-center" />}
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void confirm()}
              data-testid="pdapp-icon-confirm"
              className="px-3 py-1.5 text-[14px] leading-4 font-semibold rounded-lg hover:brightness-125 transition inline-flex items-baseline gap-1.5 disabled:opacity-40"
              // The theme's PRIMARY as text — the same colour as the solid
              // Customize — so each theme and a brand override apply to it.
              style={{ color: 'var(--btn-primary-bg)' }}
            >
              {pending === 'confirm' && <Loader2 size={12} className="animate-spin" />}
              {t('confirm', { ns: 'common' })}
            </button>
          </div>
        {error && <p className="mt-1.5 text-[11px] text-red-400" data-testid="pdapp-icon-error">{error}</p>}
      </>

      {dialogOpen && (
        <Suspense fallback={<ModalLoading />}>
          <AddPdappIconDialog
            // The chooser first — Design one or Copy from a PDApp — exactly as
            // the editor's toolbar opens it (a designed icon opens its maker). It owns the maker, the app list,
            // the search and the "open the app on click" option from here.
            initialStep="choose"
            excludeFolderId={excludeFolderId}
            // Customize twice is an EDIT, not a reset: a designed icon reopens in
            // the maker holding it (its uploaded art too), a copied one keeps its
            // "open the app on click" choice.
            // The PORTABLE spec carries `glyph`/`assets`, which the strict
            // schema rejects — `editableIconSpec` strips them, or the maker
            // silently reopens on the default.
            initialSpec={chosen.designed ? editableIconSpec(chosen.spec) : null}
            initialAssets={chosen.designed ? chosen.assets : undefined}
            initialLink={!!chosen.link}
            placement={placement}
            note={request.note}
            tenantId={getActiveTenantId() || undefined}
            onChoose={choose}
            onClose={() => setDialogOpen(false)}
          />
        </Suspense>
      )}
    </Shell>
  )
}

/** Shown while a dialog's chunk downloads. Until the lazy boundaries actually
 *  split (they did not), this state was unreachable and a `null` fallback cost
 *  nothing; now the press has to say it did something. */
function ModalLoading() {
  const { t } = useTranslation('agent')
  return (
    <div className="mt-2 flex items-center gap-2 text-[11px] themed-text-muted" data-testid="pdapp-icon-loading">
      <Loader2 size={12} className="animate-spin" /> {t('pdappIcon.opening')}
    </div>
  )
}

/** The tile the card shows. `null` draws the name-derived default, which is
 *  exactly what Confirm, uncustomized, produces — so the preview is never a promise the
 *  answer does not keep. */
function Preview({ spec, assets, size = 34 }: { spec: MiniappIconSpec | null; assets?: { bg?: string; fg?: string }; size?: number }) {
  return (
    <span className="shrink-0 flex items-center justify-center" style={{ width: size, height: size }} data-testid="pdapp-icon-preview">
      <MiniappIcon spec={spec} name={ICON_NAME} size={size} previewAssets={assets} />
    </span>
  )
}

/** The card's frame — the title and what the card is for. The agent's own note is shown by the
 *  DIALOG (it says why it was asked). Customize opens a modal, so the card has
 *  no steps and no back affordance. */
function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('agent')
  return (
    <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 my-2" data-testid="pdapp-icon-request">
      <div className="flex items-start gap-2">
        <ImageIcon size={14} className="shrink-0 mt-0.5 text-indigo-400" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium themed-text">{t('pdappIcon.title')}</p>
          <p className="mt-0.5 text-[11px] themed-text-muted">{t('pdappIcon.subtitle')}</p>
          {children}
        </div>
      </div>
    </div>
  )
}

/** Narrow a parsed request to what the card renders. Kept beside the card so a
 *  new field is added in one place. */
export function pdappIconCardProps(id: string, req: { note: string | null }): Props['request'] {
  return { id, note: req.note }
}
